import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  HostListener,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { MenuModule } from 'primeng/menu';
import { TableOverlayLineComponent } from './table-overlay-line/table-overlay-line.component';

import { AsyncPipe, NgClass, NgStyle } from '@angular/common';
import { LassoSelectionDirective } from '../../../../directives/lasso-selection.directive';
import { Confirmation, ConfirmationService, SharedModule } from 'primeng/api';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DeletionDialogComponent } from '../../../dialogs/deletion-dialog/deletion-dialog.component';
import { EditDialogComponent } from '../../../dialogs/edit-dialog/edit-dialog.component';
import { TableOverlayConfirmButtonComponent } from './table-overlay-confirm-button/table-overlay-confirm-button.component';
import { TableOverlayContextMenuComponent } from './table-overlay-context-menu/table-overlay-context-menu.component';
import { TableOverlayColumnAssignmentComponent } from './table-overlay-column-assignment/table-overlay-column-assignment.component';
import { TableOverlayMoveIntersectionComponent } from './table-overlay-move-intersection/table-overlay-move-intersection.component';
import { TableOverlayPreviewLinesComponent } from './table-overlay-preview-lines/table-overlay-preview-lines.component';
import { TableOverlayRowComponent } from './table-overlay-row/table-overlay-row.component';
import { ExtractedTableCreationService } from '../../../../service/extracted-data/extracted-table-creation.service';
import { ToolbarService } from '../../../../service/app-state/toolbar.service';
import { OcrService } from '../../../../service/ocr/ocr.service';
import { TableDetectionApiService } from '../../../../service/api/table-detection-api.service';
import { ExtractedDataStoreService } from '../../../../service/extracted-data/extracted-data-store.service';
import { ExtractedTableRemovalService } from '../../../../service/extracted-data/extracted-table-removal.service';
import { TableOverlayBackButtonComponent } from './table-overlay-back-button/table-overlay-back-button.component';
import {
  CtPage,
  Interval,
  OverlayDimensionService,
  RectDisplayStylePipe,
  scaleRect,
  WordRect,
} from '@ps-gui/claims-terminal-shared';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { associate, associateBy, keys, mapValues, simpleEquals } from '@ps-gui/shared-utils';
import { LineOrientation, NOT_IMPORTANT_TABLE_HEADER } from '../../../../models/interfaces';
import { ExtractedRow, TableReference } from '../../../../models/extracted-values';
import { TableData, TableOverlayLine } from '../../../../table-overlay/table-overlay';
import { CtBatchStoreService } from '../../../../service/app-state/ct-batch-store.service';
import { getTableModel } from '../../../../models/extraction-models/extraction-models';
import { FloatingElementDirective, Keys } from '@ps-gui/shared-core';
import { TableOverlayService } from '../../../../service/overlay/table-overlay.service';
import { debounce, distinctUntilChanged, interval, of } from 'rxjs';
import { isTableModel } from '../../../../models/domain-extraction-specifications';

export type TableOverlayType = 'table-extraction' | 'table-row-classification';

@Component({
  selector: 'ct-extraction-table-overlay',
  imports: [
    MenuModule,
    RectDisplayStylePipe,
    TableOverlayLineComponent,
    NgStyle,
    NgClass,
    ConfirmDialogModule,
    SharedModule,
    DeletionDialogComponent,
    EditDialogComponent,
    TableOverlayConfirmButtonComponent,
    TableOverlayContextMenuComponent,
    TableOverlayColumnAssignmentComponent,
    TableOverlayMoveIntersectionComponent,
    TableOverlayPreviewLinesComponent,
    TableOverlayRowComponent,
    TableOverlayBackButtonComponent,
    FloatingElementDirective,
    AsyncPipe,
  ],
  templateUrl: './table-overlay.component.html',
  styleUrl: './table-overlay.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TableOverlayComponent {
  protected readonly lassoSelection = inject(LassoSelectionDirective);
  private readonly confirmationService = inject(ConfirmationService);
  protected readonly toolbarService = inject(ToolbarService);
  private readonly tableDetectionApiService = inject(TableDetectionApiService);

  private readonly tableOverlayService = inject(TableOverlayService);
  private readonly ocrService = inject(OcrService);

  private readonly extractedDataStoreService = inject(ExtractedDataStoreService);
  protected extractedTableRemovalService = inject(ExtractedTableRemovalService);
  private readonly extractedTableCreationService = inject(ExtractedTableCreationService);

  private readonly overlayDimensionService = inject(OverlayDimensionService);
  private readonly batchStoreService = inject(CtBatchStoreService);

  private readonly destroyRef = inject(DestroyRef);

  readonly isTableCreationToggled = input.required<boolean>();
  readonly tableData = input.required<TableData>();
  readonly page = input.required<CtPage>();
  readonly type = input.required<TableOverlayType>();

  protected readonly pageId = computed(() => this.page().id);
  protected readonly documentId = computed(
    () => this.batchStoreService.getDocumentForPage(this.pageId()).id,
  );

  protected readonly dimension = computed(
    () => this.overlayDimensionService.dimensionsByPageId()[this.pageId()],
  );

  protected readonly allLines = computed(() => {
    const table = this.tableData();
    return table.lines.vertical().concat(table.lines.horizontal());
  });

  deleteTable = output<string>();
  update = output<TableData>();

  readonly deletionDialog = viewChild.required<Confirmation>('deletionDialog');
  readonly editDialog = viewChild.required<Confirmation>('editDialog');

  protected readonly currentDragInfos = signal<
    { line: TableOverlayLine; offsetRestriction: Interval }[]
  >([]);

  readonly arePreviewLinesEnabled = signal(true);
  ignoredRowIndices = new Set<number>();

  private readonly tableModel = computed(
    () => getTableModel(this.batchStoreService.ctBatch.domain, this.tableData().tableModelKey)!,
  );

  protected readonly tableColumnLabelsByKeys = computed(() => {
    return associate(
      (this.tableModel()?.fieldModels ?? []).filter((model) => !model.inlineConsolidatorForField),
      (model) => model.key,
      (model) => model.label.text,
    );
  });

  constructor() {
    this.initializeLineMovement();
  }

  @HostListener('window:mouseup') onMouseUp() {
    if (this.currentDragInfos().length > 0) this.dropLines();
  }

  protected readonly tableRect = computed<WordRect>(() => {
    return {
      x: [
        this.tableData().lines.vertical().at(0)!.offset(),
        this.tableData().lines.vertical().at(-1)!.offset(),
      ],
      y: [
        this.tableData().lines.horizontal().at(0)!.offset(),
        this.tableData().lines.horizontal().at(-1)!.offset(),
      ],
    };
  });

  protected readonly absoluteTableRect = computed<WordRect>(() =>
    scaleRect(this.tableRect(), this.dimension()),
  );

  protected isInInterval(value: number, interval: Interval) {
    return value >= interval[0] && value <= interval[1];
  }

  protected readonly isMouseOverTable = computed(() => {
    const mousePosition = this.lassoSelection.currentMousePosition();
    return (
      this.isInInterval(mousePosition[0], this.tableRect().x) &&
      this.isInInterval(mousePosition[1], this.tableRect().y)
    );
  });

  protected readonly isOverlayMenuVisible$ = toObservable(this.isMouseOverTable).pipe(
    debounce((mouseOver) => (mouseOver ? of(true) : interval(1000))),
    distinctUntilChanged(),
  );

  protected readonly isTableLineModificationAllowed = computed(
    () =>
      !(
        this.tableData().layoutDefined() ||
        this.tableData().confirmed() ||
        Keys.Control.isPressed() ||
        Keys.Alt.isPressed()
      ),
  );

  private initializeLineMovement() {
    effect(() => {
      const mousePosition = this.lassoSelection.currentMousePosition();

      if (this.currentDragInfos().length === 0) {
        return;
      }

      for (const { line, offsetRestriction } of this.currentDragInfos()) {
        if (
          line.orientation === LineOrientation.VERTICAL &&
          !this.isInInterval(mousePosition[0], offsetRestriction)
        ) {
          continue;
        }

        if (
          line.orientation === LineOrientation.HORIZONTAL &&
          !this.isInInterval(mousePosition[1], offsetRestriction)
        ) {
          continue;
        }

        line.offset.set(
          line.orientation === LineOrientation.VERTICAL ? mousePosition[0] : mousePosition[1],
        );
      }
    });
  }

  protected onOverlayMouseDown(mouseEvent: MouseEvent) {
    if (this.tableData().layoutDefined()) return;

    mouseEvent.preventDefault();
    mouseEvent.stopPropagation();

    this.addLineAtPosition();
  }

  protected addLineAtPosition(): void {
    if (this.tableData().layoutDefined()) return;

    const currentMousePosition = this.lassoSelection.currentMousePosition();
    const verticalLine: TableOverlayLine = {
      orientation: LineOrientation.VERTICAL,
      offset: signal(currentMousePosition[0]),
    };
    const horizontalLine: TableOverlayLine = {
      orientation: LineOrientation.HORIZONTAL,
      offset: signal(currentMousePosition[1]),
    };

    if (Keys.Control.isPressed() || Keys.Alt.isPressed()) {
      const targetLines = this.tableData().lines.vertical;

      const insertionIndex = targetLines().findIndex((l) => l.offset() > verticalLine.offset());
      targetLines.update((lines) => lines.toSpliced(insertionIndex, 0, verticalLine));

      this.adjustColumnHeaderAfterNewLine(insertionIndex);
    }
    if (!Keys.Control.isPressed() || Keys.Alt.isPressed()) {
      const targetLines = this.tableData().lines.horizontal;

      const insertionIndex = targetLines().findIndex((l) => l.offset() > horizontalLine.offset());
      targetLines.update((lines) => lines.toSpliced(insertionIndex, 0, horizontalLine));
    }

    this.emitUpdate();
  }

  protected dropLines() {
    this.emitUpdate();
    this.currentDragInfos.set([]);
  }

  protected deleteLine(line: TableOverlayLine) {
    const equalOrientedLines = this.getLines(line.orientation);
    const lineIndex = equalOrientedLines().findIndex((l) => l === line);
    if (line.orientation === LineOrientation.VERTICAL) {
      this.adjustColumnHeadersAfterLineDelete(lineIndex);
    }
    equalOrientedLines.update((lines) => lines.toSpliced(lineIndex, 1));
    this.currentDragInfos.set([]);
    this.arePreviewLinesEnabled.set(true);
    this.emitUpdate();
  }

  protected pickLines(...linesWithIndices: { line: TableOverlayLine; index: number }[]) {
    this.currentDragInfos.set([]);
    for (const { line, index } of linesWithIndices) {
      const lines = this.getLines(line.orientation)();
      const previousLine = index > 0 ? lines[index - 1] : undefined;
      const nextLine = index < lines.length - 1 ? lines[index + 1] : undefined;
      this.currentDragInfos.update((dragInfos) => [
        ...dragInfos,
        {
          line,
          offsetRestriction: [previousLine?.offset() ?? 0, nextLine?.offset() ?? 1],
        },
      ]);
    }
  }

  protected getLines(orientation: LineOrientation) {
    return orientation === LineOrientation.VERTICAL
      ? this.tableData().lines.vertical
      : this.tableData().lines.horizontal;
  }

  toggleRowDisplay(rowIndex: number) {
    if (this.ignoredRowIndices.has(rowIndex)) {
      this.ignoredRowIndices.delete(rowIndex);
    } else {
      this.ignoredRowIndices.add(rowIndex);
    }
  }

  private emitUpdate() {
    this.update.emit(this.tableData());
  }

  confirmButtonLogic() {
    if (!this.tableData().layoutDefined()) {
      this.tableData().layoutDefined.set(true);
      this.preFillColumnHeaders();
      this.emitUpdate();
    } else {
      const createdTable = this.tableOverlayService.createTable(
        this.tableData(),
        this.tableModel(),
        this.ignoredRowIndices,
      );
      this.tableData().confirmed.set(true);
      this.emitUpdate();

      this.extractedTableCreationService.createTable(createdTable);
      this.toolbarService.setTableCreationEnabled(false);
    }
  }

  private preFillColumnHeaders() {
    if (this.tableData().columnHeaders().length > 0) return;

    this.tableData().columnHeaders.set(
      Array<string | undefined>(this.tableData().lines.vertical().length - 1).fill(undefined),
    );
  }

  undoLayoutDefined() {
    this.tableData().layoutDefined.set(false);
    this.emitUpdate();
  }

  private adjustColumnHeaderAfterNewLine(insertionIndex: number) {
    if (this.tableData().columnHeaders().length === 0) return;

    this.tableData().columnHeaders.update((headers) =>
      headers.with(insertionIndex - 1, undefined).toSpliced(insertionIndex, 0, undefined),
    );
  }

  private adjustColumnHeadersAfterLineDelete(deletionIndex: number) {
    if (this.tableData().columnHeaders().length === 0) return;

    this.tableData().columnHeaders.update((headers) =>
      headers.with(deletionIndex - 1, undefined).toSpliced(deletionIndex, 1),
    );
  }

  detectTable() {
    const words = this.ocrService.getWordsInRect(this.page().id, this.tableRect());
    this.tableDetectionApiService
      .detectTable(words)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([columns, rows]) => {
        const innerColumnLines = columns.slice(1).map((columnOffset) => ({
          offset: signal(columnOffset),
          orientation: LineOrientation.VERTICAL,
        }));

        const innerRowLines = rows.slice(1).map((rowOffset) => ({
          offset: signal(rowOffset),
          orientation: LineOrientation.HORIZONTAL,
        }));

        const lines = this.tableData().lines;
        lines.vertical.update((oldLines) => [
          oldLines[0],
          ...innerColumnLines,
          oldLines[oldLines.length - 1],
        ]);

        lines.horizontal.update((oldLines) => [
          oldLines[0],
          ...innerRowLines,
          oldLines[oldLines.length - 1],
        ]);

        const columnCount = lines.vertical().length - 1;
        this.tableData().columnHeaders.set(Array(columnCount).fill(undefined));
      });
  }

  openDeletionDialog(): void {
    this.deletionDialog().key = `deletionDialog-${this.tableData().pageId}-${this.tableData().id}`;
    this.confirmationService.confirm(this.deletionDialog());
  }

  async openEditDialog(): Promise<void> {
    this.editDialog().key = `editDialog-${this.tableData().pageId}-${this.tableData().id}`;
    const associatedPositions = Object.values(
      await this.extractedDataStoreService.getExtractedTables(this.documentId()),
    )
      .filter((row) => row !== undefined)
      .flatMap((rows) => rows)
      .filter((row) => (row.rowMembership as TableReference).tableId === this.tableData().id);
    if (this.wasTableDataModifiedManually(associatedPositions)) {
      this.confirmationService.confirm(this.editDialog());
      return;
    }

    this.editDialogConfirmLogic();
  }

  editDialogConfirmLogic() {
    this.toolbarService.setTableCreationEnabled(true);
    this.extractedTableRemovalService.destroyTable(this.tableData().id);
    this.tableData().confirmed.set(false);
    this.tableData().layoutDefined.set(false);

    this.emitUpdate();
  }

  private wasTableDataModifiedManually(storedRows: ExtractedRow[]) {
    const createdTable = this.tableOverlayService.createTable(
      this.tableData(),
      this.tableModel(),
      this.ignoredRowIndices,
    );
    const associateRowsByIndex = (rows: ExtractedRow[]) =>
      associateBy(rows, (row) => (row.rowMembership as TableReference).originalTableRowIndex!);
    const ocrRowsByRowIndex = associateRowsByIndex(createdTable.rows);
    const storedRowsByRowIndex = associateRowsByIndex(storedRows);

    const indices = new Set([...keys(ocrRowsByRowIndex), ...keys(storedRowsByRowIndex)]);
    const rowIndicesEqual =
      indices.size === Object.keys(ocrRowsByRowIndex).length &&
      indices.size === Object.keys(storedRowsByRowIndex).length;

    if (!rowIndicesEqual) {
      return true;
    }

    return indices.values().some(
      (index) =>
        !simpleEquals(
          mapValues(ocrRowsByRowIndex[index].rowData, (_, cell) => cell.value),
          mapValues(storedRowsByRowIndex[index].rowData, (_, cell) => cell.value),
        ),
    );
  }

  setNewTableHeaderForColumn(columnIndex: number, newHeader: string) {
    this.setSameHeaderToUndefined(newHeader);
    this.tableData().columnHeaders.update((headers) => headers.with(columnIndex, newHeader));
  }

  private setSameHeaderToUndefined(header: string) {
    if (header !== NOT_IMPORTANT_TABLE_HEADER) {
      const columnIndexWithSameLabel = this.tableData()
        .columnHeaders()
        .findIndex((h) => {
          return h === header;
        });

      if (columnIndexWithSameLabel !== -1) {
        this.tableData().columnHeaders.update((headers) =>
          headers.with(columnIndexWithSameLabel, undefined),
        );
      }
    }
  }

  deleteDialogConfirmLogic() {
    this.extractedTableRemovalService.destroyTable(this.tableData().id);
    this.toolbarService.setTableCreationEnabled(false);
    this.deleteTable.emit(this.tableData().id);
  }

  protected readonly isTableModel = isTableModel;
}
